import dayjs from 'dayjs';
import {
  TranslateFunc,
  FormatDateFunc,
  CurrencyFormatFunc,
  CurrencyFormat,
} from '@ha/intl';
import {
  FeeMode,
  getPricingStructure,
  PricingStructure,
} from 'ha/modules/Pricing';
import {
  ListingCostPayableByValues,
  ListingCostGroupValues,
  Listing,
  ListingPricingType,
} from '@ha/api/v2/types/Listing';
import {
  Instalment,
  RentalConditions,
} from '@ha/api/v2/types/RentalConditions';
import { UnitType } from '@ha/api/v2/types/inventory/UnitType';
import {
  COSTS_TRANSLATIONS,
  COSTS_PAYABLE_AT_TRANSLATIONS,
  COSTS_REQUIRED_TRANSLATIONS,
} from './translations';
import { SHOW_ESTIMATED_COSTS } from '../ListingEdit/components/StructuredCostsFieldGroup/constants';

interface Row {
  caption: string;
  value: string;
  crossedPrice?: string;
  originalValue?: number;
  tooltip?: string;
  date?: string;
}

interface Section {
  key?: string;
  title: {
    text: string;
    tooltip?: string;
  };
  rows: Row[];
  total?: string;
  dueOn?: string;
}

interface Params {
  listing: Listing | UnitType;
  rentalConditions?: RentalConditions;
  feeMode: FeeMode;
  formatDate: FormatDateFunc;
  formatCurrency: CurrencyFormatFunc;
  T: TranslateFunc;
  isAdvertiserVariant?: boolean;
}

export function getPaymentSections({
  listing,
  rentalConditions,
  feeMode,
  isAdvertiserVariant,
  formatDate,
  formatCurrency,
  T,
}: Params): Section[] {
  const pricingStructure = getPricingStructure({ rentalConditions });
  const actualRentalConditions: Pick<
    RentalConditions,
    | 'firstInstalment'
    | 'tenantFee'
    | 'currency'
    | 'costs'
    | 'costGroups'
    | 'instalments'
    | 'contractValue'
    | 'startDate'
    | 'pricingData'
  > = rentalConditions ?? {
    firstInstalment: 0,
    tenantFee: 0,
    currency: '',
    costs: {},
    costGroups: {},
    instalments: [] as RentalConditions['instalments'],
    contractValue: 0,
    startDate: '',
    pricingData: { flatPrice: 0 },
  };

  let feeVal = 0;

  const currencyFormat =
    actualRentalConditions.currency === 'EUR'
      ? CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION_NO_GROUPING
      : CurrencyFormat.WITH_CURRENCY_CODE_NO_FRACTION_NO_GROUPING;

  if (feeMode === FeeMode.regular) {
    feeVal = actualRentalConditions.tenantFee;
  }
  const firstMonthRent =
    pricingStructure === PricingStructure.instalment
      ? actualRentalConditions.firstInstalment
      : actualRentalConditions.pricingData.flatPrice || listing.price;
  const payedAtBookingSection: Section = {
    title: {
      text: isAdvertiserVariant
        ? T('payment_overview.advertiser.paid_to_HA.subtitle')
        : T('StructuredCosts.LDP.paytobook'),
    },
    rows: [
      {
        caption: T("First month's rent"),
        value: formatCurrency(
          firstMonthRent,
          actualRentalConditions.currency,
          currencyFormat,
        ),
        tooltip: isAdvertiserVariant
          ? ''
          : T('paymentOverview.first_rent_tooltop'),
      },
      ...(feeMode === FeeMode.regular && !isAdvertiserVariant
        ? [
            {
              caption: T('paymentOverview.ha_fee'),
              value: formatCurrency(
                feeVal,
                actualRentalConditions.currency,
                currencyFormat,
              ),
              tooltip: isAdvertiserVariant
                ? ''
                : T('paymentOverview.ha_fee_tooltip'),
            },
          ]
        : []),
    ],
    total: isAdvertiserVariant
      ? ''
      : formatCurrency(
          firstMonthRent + feeVal,
          actualRentalConditions.currency,
          currencyFormat,
        ),
    dueOn: isAdvertiserVariant
      ? ''
      : T('paymentOverview.pay_when_booking_is_confirmed.due'),
  };

  const duringYourStaySection: Section = {
    title: {
      text: isAdvertiserVariant
        ? T('PaymentOverview.Landlord.Costs')
        : T('StructuredCosts.LDP.duringyourstay'),
    },
    rows:
      pricingStructure === PricingStructure.instalment
        ? []
        : [
            {
              caption: T('Monthly rent'),
              value: formatCurrency(
                actualRentalConditions.pricingData.flatPrice || listing.price,
                actualRentalConditions.currency,
                currencyFormat,
              ),
            },
          ],
  };

  const optionalSection: Section = {
    title: {
      text: T('StructuredCosts.create.optionalservices'),
      tooltip: T('StructuredCosts.optionalservicesdescription'),
    },
    rows: [],
  };

  const depositSection: Section = {
    title: {
      text: T('paymentOverview.deposits'),
    },
    rows: [],
  };
  let instalmentRows;
  if (pricingStructure === PricingStructure.instalment) {
    const firstInstalmentRow = {
      caption: `${formatDate(
        'MMMM YYYY',
        actualRentalConditions.startDate,
      )} (${T('payment_overview.rent.first_payment.paid_to_HA')})`,

      value: formatCurrency(
        actualRentalConditions.firstInstalment,
        actualRentalConditions.currency,
        currencyFormat,
      ),
    };
    instalmentRows = [
      firstInstalmentRow,
      ...actualRentalConditions.instalments.map(
        (instalment: Instalment): Row => {
          const instalmentDate = dayjs(instalment.dueDate)
            .add(1, 'month')
            .format();

          let crossedPrice = '';
          if (listing.pricingType === ListingPricingType.MONTHLY) {
            const monthlyPriceItem =
              actualRentalConditions.pricingData?.monthlyPrices?.prices.find(
                (item: { month: string; price: number }) =>
                  item.month.includes(`-${formatDate('MM', instalmentDate)}`),
              );
            const monthlyPrice = monthlyPriceItem?.price;
            crossedPrice =
              monthlyPrice && monthlyPrice !== instalment.amount
                ? formatCurrency(
                    monthlyPrice,
                    actualRentalConditions.currency,
                    currencyFormat,
                  )
                : '';
          } else if (
            listing.pricingType === ListingPricingType.FLAT &&
            actualRentalConditions.pricingData.flatPrice
          ) {
            crossedPrice =
              actualRentalConditions.pricingData.flatPrice !== instalment.amount
                ? formatCurrency(
                    actualRentalConditions.pricingData.flatPrice,
                    actualRentalConditions.currency,
                    currencyFormat,
                  )
                : '';
          }
          return {
            caption: formatDate('MMMM YYYY', instalmentDate),
            crossedPrice,
            value: formatCurrency(
              instalment.amount,
              actualRentalConditions.currency,
              currencyFormat,
            ),
          };
        },
      ),
    ];
  } else {
    instalmentRows = actualRentalConditions.instalments.map(
      (instalment: Instalment, index: number): Row => ({
        caption: T('paymentOverview.Instalment %s', index + 2),
        date: `${T('paymentOverview.due')}: ${formatDate(
          'D MMM YYYY',
          instalment.dueDate,
        )}`,
        value: formatCurrency(
          instalment.amount,
          actualRentalConditions.currency,
          currencyFormat,
        ),
      }),
    );
  }

  const instalmentsSection: Section = {
    key: 'instalments',
    title: {
      text: isAdvertiserVariant
        ? T('Rent')
        : T('paymentOverview.RentInstalment_header'),
    },
    rows: instalmentRows,
    total: formatCurrency(
      actualRentalConditions.contractValue,
      actualRentalConditions.currency,
      currencyFormat,
    ),
  };

  if (actualRentalConditions.costs !== null) {
    Object.keys(actualRentalConditions.costs).forEach((cost: string) => {
      const listingCost = actualRentalConditions.costs[cost];
      const costType = actualRentalConditions.costGroups[cost];
      const caption = T(COSTS_TRANSLATIONS[cost]);
      const payableAt = T(COSTS_PAYABLE_AT_TRANSLATIONS[listingCost.payableAt]);
      const value = `${formatCurrency(
        listingCost.value,
        actualRentalConditions.currency,
        currencyFormat,
      )}
        ${
          listingCost.isEstimated && SHOW_ESTIMATED_COSTS.includes(cost)
            ? ` ${T('StructuredCosts.Estimated')}`
            : ''
        }`;

      if (costType === ListingCostGroupValues.ADDITIONAL) {
        if (
          listingCost.payableBy === ListingCostPayableByValues.NOT_APPLICABLE
        ) {
          duringYourStaySection.rows.push({
            caption: `${caption} (${payableAt})`,
            value: T('StructuredCosts.create.nonapplicable'),
          });
        } else if (
          listingCost.payableBy !== ListingCostPayableByValues.INCLUDED_IN_RENT
        ) {
          duringYourStaySection.rows.push({
            caption: `${caption} (${payableAt})`,
            value,
          });
        }
      }

      if (costType === ListingCostGroupValues.DEPOSIT) {
        depositSection.rows.push({
          caption:
            caption +
            (listingCost.required
              ? ` (${T(COSTS_REQUIRED_TRANSLATIONS.required)})`
              : ` (${T(COSTS_REQUIRED_TRANSLATIONS.optional)})`),
          value,
          originalValue: listingCost.value,
        });
      }

      if (costType === ListingCostGroupValues.OPTIONAL) {
        if (
          listingCost.payableBy !== ListingCostPayableByValues.INCLUDED_IN_RENT
        ) {
          optionalSection.rows.push({
            caption: `${caption} (${payableAt})`,
            value,
          });
        }
      }
    });
  }

  const sections: Section[] = isAdvertiserVariant
    ? [
        payedAtBookingSection,
        instalmentsSection,
        depositSection,
        duringYourStaySection,
        optionalSection,
      ]
    : [
        payedAtBookingSection,
        depositSection,
        duringYourStaySection,
        instalmentsSection,
        optionalSection,
      ];

  return sections;
}
